/**
 * lead-time-badge.js
 * Fallback variant switching for the lead time badge.
 *
 * Primary mechanism: the badge container uses data-dynamic-variant-content so
 * the Enterprise theme's own variant JS updates it automatically via section
 * re-render. This script is a belt-and-braces fallback that also watches the
 * hidden variant ID input and updates the badge directly if the theme's
 * mechanism hasn't already done so.
 */
(function () {
  'use strict';

  var LT_MAP = {
    'In Stock':       { state: 'fast',    label: 'Ships in',      value: 'In stock – ready to go', icon: 'clock' },
    '1-2 WD':         { state: 'fast',    label: 'Ships in',      value: '1 – 2 working days',     icon: 'clock' },
    '1-3 WD':         { state: 'fast',    label: 'Ships in',      value: '1 – 3 working days',     icon: 'clock' },
    '1-5 WD':         { state: 'fast',    label: 'Ships in',      value: '1 – 5 working days',     icon: 'clock' },
    '2-3 WD':         { state: 'fast',    label: 'Ships in',      value: '2 – 3 working days',     icon: 'clock' },
    '2-5 WD':         { state: 'mid',     label: 'Ships in',      value: '2 – 5 working days',     icon: 'clock' },
    'MTO 2-5 WD':     { state: 'mto',     label: 'Made to order', value: '2 – 5 working days',     icon: 'box'   },
    '3-5 WD':         { state: 'mid',     label: 'Ships in',      value: '3 – 5 working days',     icon: 'clock' },
    '5-7 WD':         { state: 'mid',     label: 'Ships in',      value: '5 – 7 working days',     icon: 'clock' },
    '5-10 WD':        { state: 'mid',     label: 'Ships in',      value: '5 – 10 working days',    icon: 'clock' },
    '7-10 WD':        { state: 'mid',     label: 'Ships in',      value: '7 – 10 working days',    icon: 'clock' },
    '10-15 WD':       { state: 'slow',    label: 'Ships in',      value: '10 – 15 working days',   icon: 'clock' },
    '10-20 WD':       { state: 'slow',    label: 'Ships in',      value: '10 – 20 working days',   icon: 'clock' },
    '15-20 WD':       { state: 'slow',    label: 'Ships in',      value: '15 – 20 working days',   icon: 'clock' },
    '20-28 WD':       { state: 'slow',    label: 'Ships in',      value: '20 – 28 working days',   icon: 'clock' },
    'MTO Contact Us': { state: 'contact', label: 'Lead time',     value: 'Contact us for details',      icon: 'phone' },
    'Contact Us':     { state: 'contact', label: 'Lead time',     value: 'Contact us for details',      icon: 'phone' }
  };

  var ICONS = {
    clock: '<svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><circle cx="12" cy="12" r="10"/><polyline points="12 6 12 12 16 14"/></svg>',
    box:   '<svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><path d="M21 16V8a2 2 0 0 0-1-1.73l-7-4a2 2 0 0 0-2 0l-7 4A2 2 0 0 0 3 8v8a2 2 0 0 0 1 1.73l7 4a2 2 0 0 0 2 0l7-4A2 2 0 0 0 21 16z"/></svg>',
    phone: '<svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><path d="M22 16.92v3a2 2 0 0 1-2.18 2 19.79 19.79 0 0 1-8.63-3.07A19.5 19.5 0 0 1 4.69 12 19.79 19.79 0 0 1 1.61 3.4 2 2 0 0 1 3.6 1.22h3a2 2 0 0 1 2 1.72c.127.96.361 1.903.7 2.81a2 2 0 0 1-.45 2.11L7.91 8.77a16 16 0 0 0 6.29 6.29l.95-.95a2 2 0 0 1 2.11-.45c.907.339 1.85.573 2.81.7A2 2 0 0 1 22 16.92z"/></svg>'
  };

  var variantData = null;

  function getVariantData() {
    if (variantData) return variantData;
    var el = document.getElementById('lt-variant-data');
    if (!el) return null;
    try { variantData = JSON.parse(el.textContent); } catch (e) { return null; }
    return variantData;
  }

  function renderBadge(container, variantId) {
    var data = getVariantData();
    if (!data) return;

    var raw = (data[String(variantId)] || '').trim();
    var info = LT_MAP[raw];

    if (!info) {
      container.hidden = true;
      container.innerHTML = '';
      return;
    }

    /* Only update if the badge state has actually changed — avoids overwriting
       a freshly-rendered Liquid badge that the theme just injected. */
    var existing = container.querySelector('[data-lt-badge]');
    if (existing && existing.classList.contains('lt-badge--' + info.state)) return;

    container.hidden = false;
    container.innerHTML =
      '<div class="lt-badge lt-badge--' + info.state + ' lt-badge--lg lt-badge--fw" data-lt-badge>' +
        '<span class="lt-badge__icon" aria-hidden="true">' + ICONS[info.icon] + '</span>' +
        '<span class="lt-badge__inner">' +
          '<span class="lt-badge__label">' + info.label + '</span>' +
          '<span class="lt-badge__value">' + info.value + '</span>' +
        '</span>' +
      '</div>';
  }

  function init() {
    var container = document.getElementById('lt-badge-container');
    if (!container) return;

    /* Watch the hidden variant ID input. The theme sets its value attribute
       via setAttribute() when swapping variant content. */
    var idInput = document.querySelector('input[name="id"][data-dynamic-variant-content]');
    if (idInput) {
      new MutationObserver(function () {
        renderBadge(container, idInput.value);
      }).observe(idInput, { attributes: true, attributeFilter: ['value'] });
    }

    /* Also catch direct variant picker changes (fires before theme re-render,
       so we schedule after a tick to read the updated ID). */
    document.addEventListener('change', function (e) {
      var form = e.target.closest('.js-product-form-main');
      if (!form) return;
      if (e.target.name === 'id') return; // skip the hidden input itself
      setTimeout(function () {
        var input = form.querySelector('input[name="id"]');
        if (input) renderBadge(container, input.value);
      }, 120);
    });
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
}());
